import { Upload, FileText, X, LoaderCircle } from 'lucide-react';
import { Dispatch, SetStateAction, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { CSVType } from '@/services/CSVService';

interface FileUploadProps {
  onFileUpload: (file: File) => void;
  isLoading?: boolean;
  selectedFile: File | null;
  onSetSelectedFile: Dispatch<SetStateAction<File | null>>;
  sheetType: CSVType;
}

export function FileUpload({
  onFileUpload,
  selectedFile,
  isLoading = false,
  onSetSelectedFile,
  sheetType,
}: FileUploadProps) {
  const { t: tCommon } = useTranslation('translation', {
    keyPrefix:
      'pages.Settings.components.ProductsManager.components.FileUpload',
  });
  const { t } = useTranslation('translation', {
    keyPrefix: `pages.Settings.components.ProductsManager.${sheetType}.components.FileUpload`,
  });
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const csvFile = acceptedFiles.find(
        (file) => file.type === 'text/csv' || file.name.endsWith('.csv'),
      );
      if (csvFile) {
        onSetSelectedFile(csvFile);
      }
    },
    [onSetSelectedFile],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
    },
    multiple: false,
  });

  function removeFile() {
    onSetSelectedFile(null);
  }

  async function handleUpload() {
    if (selectedFile && !isLoading) {
      onFileUpload(selectedFile);
    }
  }

  return (
    <div className="flex flex-row items-end gap-4">
      <div
        {...getRootProps()}
        className={cn(
          'w-full cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-colors',
          isDragActive
            ? 'border-primary bg-black/5'
            : 'border-muted-foreground/25 hover:border-primary/50 hover:bg-black/5',
        )}
      >
        <input {...getInputProps()} disabled={isLoading} />
        {isLoading ? (
          <LoaderCircle className="mx-auto mb-4 h-12 w-12 animate-spin text-primary" />
        ) : (
          <Upload className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
        )}
        {isLoading ? (
          <p className="font-medium text-primary">
            {tCommon('uploadingMessage')}
          </p>
        ) : isDragActive ? (
          <p className="text-primary">{tCommon('dropZoneActive')}</p>
        ) : (
          <div>
            <p className="mb-2 font-medium text-foreground">
              {t('dropZoneInactive')}
            </p>
            <p className="text-sm text-muted-foreground">
              {t('dropZoneSupport')}
            </p>
          </div>
        )}
      </div>

      <div className="space-y-2">
        {selectedFile && (
          <div className="space-y-2">
            <h4 className="font-medium">{t('selectedFilesTitle')}</h4>
            <div className="flex items-center justify-between rounded bg-muted p-2">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                <span className="text-sm">{selectedFile.name}</span>
                <span className="text-xs text-muted-foreground">
                  ({(selectedFile.size / 1024).toFixed(1)} KB)
                </span>
              </div>
              <Button variant="ghost" size="sm" onClick={removeFile}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <Button
              onClick={handleUpload}
              className="w-full"
              disabled={isLoading}
            >
              {isLoading && (
                <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
              )}
              {isLoading ? tCommon('uploadingButton') : t('uploadButton')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
